import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { derivePreviewText } from '@/lib/text';
import { getNextUpcomingOccurrence } from '@/lib/fleaMarkets/occurrences';
import { resolvePublicMediaUrl } from '@/lib/media/resolvePublicMediaUrl';

export interface MemberHomeActivity {
  id: string;
  kind: 'activity' | 'flea_market';
  title: string;
  shortDescription: string | null;
  coverImageUrl: string | null;
  startDate: string;
  endDate: string;
  timingMode: string;
  eventType: string;
  outingTags: string[];
  isFree: boolean;
  lat: number | null;
  lng: number | null;
  producerName: string | null;
  producerSlug: string | null;
  placeName?: string | null;
  areaName?: string | null;
  href?: string;
}

async function fetchMemberHomeFleaMarkets(region: string): Promise<MemberHomeActivity[]> {
  const { data: marketRows, error: marketError } = await supabase
    .from('producer_flea_markets' as any)
    .select('id, title, description, place_id, places!inner(name, landskap, municipality, lat, lng)')
    .eq('status', 'public')
    .eq('places.landskap', region);

  if (marketError) {
    console.error('Error fetching member home flea markets:', marketError);
    return [];
  }

  const markets = (marketRows ?? []) as unknown as Array<{
    id: string;
    title: string;
    description: string | null;
    place_id: string;
    places: {
      name: string;
      landskap: string | null;
      municipality: string | null;
      lat: number | null;
      lng: number | null;
    };
  }>;

  if (markets.length === 0) return [];

  const marketIds = markets.map((market) => market.id);
  const [{ data: occurrenceRows, error: occurrenceError }, { data: coverRows, error: coverError }] = await Promise.all([
    supabase
      .from('producer_flea_market_occurrences' as any)
      .select('flea_market_id, start_at, end_at, status')
      .in('flea_market_id', marketIds),
    supabase
      .from('media_assets' as any)
      .select('owner_id, path, created_at')
      .eq('owner_type', 'flea_market')
      .eq('kind', 'cover')
      .in('owner_id', marketIds),
  ]);

  if (occurrenceError) {
    console.error('Error fetching flea market occurrences:', occurrenceError);
    return [];
  }

  if (coverError) {
    console.error('Error fetching flea market covers:', coverError);
  }

  const coversByMarketId = new Map<string, string>();
  ((coverRows ?? []) as unknown as Array<{ owner_id: string; path: string | null; created_at: string }>).
    sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .forEach((row) => {
      if (!coversByMarketId.has(row.owner_id) && row.path) {
        coversByMarketId.set(row.owner_id, resolvePublicMediaUrl(row.path) ?? '');
      }
    });

  const occurrencesByMarketId = new Map<string, Array<{ start_at: string; end_at: string | null; status?: string }>>();
  ((occurrenceRows ?? []) as unknown as Array<{ flea_market_id: string; start_at: string; end_at: string | null; status?: string }>).forEach((row) => {
    const rows = occurrencesByMarketId.get(row.flea_market_id) ?? [];
    rows.push({ start_at: row.start_at, end_at: row.end_at, status: row.status });
    occurrencesByMarketId.set(row.flea_market_id, rows);
  });

  return markets
    .map((market) => {
      const nextOccurrence = getNextUpcomingOccurrence(occurrencesByMarketId.get(market.id) ?? []);
      if (!nextOccurrence) return null;

      return {
        id: market.id,
        kind: 'flea_market' as const,
        title: market.title,
        shortDescription: derivePreviewText(market.description, 140),
        coverImageUrl: coversByMarketId.get(market.id) || null,
        startDate: nextOccurrence.start_at,
        endDate: nextOccurrence.end_at ?? nextOccurrence.start_at,
        timingMode: 'date_time',
        eventType: 'flea_market',
        outingTags: [],
        isFree: true,
        lat: market.places?.lat ?? null,
        lng: market.places?.lng ?? null,
        producerName: market.places?.name ?? null,
        producerSlug: null,
        placeName: market.places?.name ?? null,
        areaName: market.places?.landskap ?? market.places?.municipality ?? null,
        href: `/loppis/${market.id}`,
      } as MemberHomeActivity;
    })
    .filter((item): item is MemberHomeActivity => item != null);
}

async function fetchMemberHomeActivities(region: string, limit: number): Promise<MemberHomeActivity[]> {
  const [activityResult, fleaMarketItems] = await Promise.all([
    supabase.rpc('get_member_home_activities', {
      p_region: region,
      p_limit: limit,
    }),
    fetchMemberHomeFleaMarkets(region),
  ]);

  const { data, error } = activityResult;

  if (error) {
    console.error('Error fetching member home activities:', error);
    return fleaMarketItems.slice(0, limit);
  }

  const activityItems = (data ?? []).map((row: any) => ({
    id: row.id,
    kind: 'activity' as const,
    title: row.title,
    shortDescription: derivePreviewText(row.short_description || row.description, 160),
    // NOTE (media hardening v1): cover_image_url is media_assets-derived in SQL RPC, not a legacy field.
    // TODO: Rename RPC column to cover_media_path for SoT clarity.
    coverImageUrl: resolvePublicMediaUrl(row.cover_image_url),
    startDate: row.start_date,
    endDate: row.end_date,
    timingMode: row.timing_mode,
    eventType: row.event_type,
    outingTags: row.outing_tags ?? [],
    isFree: row.is_free ?? true,
    lat: row.lat,
    lng: row.lng,
    producerName: row.producer_name,
    producerSlug: row.producer_slug,
  }));

  return [...activityItems, ...fleaMarketItems]
    .sort((left, right) => new Date(left.startDate).getTime() - new Date(right.startDate).getTime())
    .slice(0, limit);
}

export function useMemberHomeActivities(region = 'Dalarna', limit = 3) {
  return useQuery({
    queryKey: ['member-home-activities', region, limit],
    queryFn: () => fetchMemberHomeActivities(region, limit),
    staleTime: 5 * 60 * 1000,
  });
}
